import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminTradesPage() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadTrades = () => {
    setLoading(true);
    adminAPI.getTrades(200).then(r => { setTrades(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadTrades(); }, []);

  const filtered = filter === 'all' ? trades : trades.filter(t => t.status === filter);

  const totalVolume = trades.reduce((s, t) => s + (t.volume * t.entry_price), 0);
  const openCount = trades.filter(t => t.status === 'open').length;
  const closedCount = trades.filter(t => t.status === 'closed').length;
  const totalPnl = trades.filter(t => t.status === 'closed').reduce((s, t) => s + (t.profit_loss || 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-trades-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Trade Monitoring</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{trades.length} total trades</p>
          </div>
          <button onClick={loadTrades} data-testid="refresh-trades-btn" className="flex items-center gap-1.5 px-4 py-2 bg-green-500/15 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/25 transition-colors border border-green-500/20">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1">Total Volume</p>
            <p className="text-lg font-bold font-mono text-[var(--text-primary)]">{formatCurrency(totalVolume)}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1">Open Trades</p>
            <p className="text-lg font-bold font-mono text-green-400">{openCount}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1">Closed Trades</p>
            <p className="text-lg font-bold font-mono text-[var(--text-primary)]">{closedCount}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1">Total P/L</p>
            <p className={`text-lg font-bold font-mono ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'open', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`admin-trade-filter-${f}`}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'text-[var(--text-secondary)] bg-[var(--bg-surface)] border border-[var(--border-subtle)]'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? trades.length : trades.filter(t => t.status === f).length})
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg overflow-x-auto" data-testid="admin-trades-table">
          {loading ? (
            <div className="p-10 text-center"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--text-secondary)]">No trades found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                  <th className="text-left px-5 py-3">Pair</th>
                  <th className="text-left px-3 py-3">User ID</th>
                  <th className="text-left px-3 py-3">Type</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-right px-3 py-3">Volume</th>
                  <th className="text-right px-3 py-3">Entry</th>
                  <th className="text-right px-3 py-3">Current</th>
                    <th className="text-right px-3 py-3">Target</th>
                    <th className="text-right px-3 py-3">SL</th>
                    <th className="text-right px-3 py-3">Spread</th>
                    <th className="text-right px-3 py-3">P/L</th>
                  <th className="text-right px-5 py-3">Opened At</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className="border-b border-[var(--divide-color)] hover:bg-[var(--hover-bg)] transition-colors">
                    <td className="px-5 py-3">
                      <span className="font-mono text-xs text-[var(--text-primary)] font-medium">{(t.symbol || t.coin_symbol)?.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-3 text-[10px] text-green-400 font-mono">#{t.user_id}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {t.type === 'buy' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {t.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${t.status === 'open' ? 'bg-green-500/15 text-green-400' : 'bg-[var(--hover-bg)] text-[var(--text-secondary)]'}`}>{t.status}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{t.volume}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{formatCurrency(t.entry_price)}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{formatCurrency(t.current_price)}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-emerald-400/70">{t.take_profit ? formatCurrency(t.take_profit) : '-'}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-red-400/70">{t.stop_loss ? formatCurrency(t.stop_loss) : '-'}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{t.spread_amount ? formatCurrency(t.spread_amount) : '-'}</td>
                    <td className={`px-3 py-3 text-right font-mono text-xs font-medium ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {t.status === 'closed' ? `${(t.profit_loss || 0) >= 0 ? '+' : ''}${formatCurrency(t.profit_loss)}` : '-'}
                    </td>
                    <td className="px-5 py-3 text-right text-[10px] text-[var(--text-secondary)]">{t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
